import { useCallback, useRef } from 'react';
import { message } from 'antd';
import { useDemand, ACTIONS } from '../../context/DemandContext';
import { useFileManagementContext } from '../../context/FileManagementContext';
import { useFileUpload } from './useFileUpload';

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

/**
 * 🎯 DemandForm 檔案管理組合 Hook
 * 封裝 DemandContext + FileManagementContext 的檔案操作
 * 類似 MainContainer 的 handleUpload 模式
 */
export const useDemandFormFileManagement = () => {
  const { state: demandState, dispatch: demandDispatch } = useDemand();
  const fileManagementContext = useFileManagementContext();
  const { handleFileUpload } = useFileUpload();
  
  // 🛡️ 防重複上傳的追蹤器
  const uploadingFiles = useRef(new Set());

  // 🎯 重試操作函數
  const retryOperation = useCallback(async (operation, maxRetries = MAX_RETRIES) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        console.warn(`操作失敗 (第 ${attempt} 次嘗試):`, error);
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
      }
    }
    
    throw lastError;
  }, []);

  // 🎯 通用的上傳處理函數 (DemandContext + FileManagementContext)
  const handleUpload = useCallback(async ({
    file,
    formField,
    fileSource = 'local'
  }) => {
    // 🛡️ 防重複上傳檢查
    const fileKey = `${file.name}_${file.size}_${file.lastModified || Date.now()}`;
    const formFieldKey = Array.isArray(formField) ? formField.join('.') : formField;
    const uploadKey = `${formFieldKey}_${fileKey}`;

    if (uploadingFiles.current.has(uploadKey)) {
      console.warn('[useDemandFormFileManagement] 檔案正在上傳中，跳過重複請求:', uploadKey);
      return;
    }

    // 🛡️ 檢查 DemandContext 中是否已有相同檔案
    const formFieldPath = getFormFieldPath(formField);
    const existingFiles = getExistingFiles(demandState, formFieldPath);
    const isDuplicate = existingFiles.some(existingFile => 
      existingFile.fileName === file.name && 
      Math.abs(Date.now() - new Date(existingFile.addedAt || 0).getTime()) < 3000
    );

    if (isDuplicate) {
      console.warn('[useDemandFormFileManagement] 檔案已存在於 DemandContext 中，跳過重複上傳:', file.name);
      return;
    }

    // 標記為正在上傳
    uploadingFiles.current.add(uploadKey);

    try {
      console.log('[useDemandFormFileManagement] handleUpload 參數:', {
        file,
        formField,
        fileSource,
        uploadKey
      });

      // 1. 將檔案上傳到 FileManagementContext (使用重試機制)
      // 🎯 useFileUpload 已經處理完整的媒體時長計算，不需要重複處理
      const result = await retryOperation(() => handleFileUpload({
        file,
        fileSource,
        formField: formField
      }));

      console.log('[useDemandFormFileManagement] handleFileUpload result:', result);

      // 2. 將檔案資訊添加到 DemandContext
      demandDispatch({
        type: ACTIONS.UPLOAD_FILE,
        payload: {
          file: {
            ...result.file,
            addedAt: new Date().toISOString() // 添加時間戳
          },
          formField: formFieldPath
        }
      });

      message.success('檔案上傳成功');
    } catch (error) {
      console.error('[useDemandFormFileManagement] 檔案上傳錯誤:', error);

      // 錯誤處理：清理檔案
      if (file.uid) {
        fileManagementContext.actions.removeFile(file.uid);
        demandDispatch({
          type: ACTIONS.REMOVE_FILE,
          payload: {
            fileId: file.uid,
            formField: formFieldPath
          }
        });
      }

      message.error(error.message || '檔案上傳失敗');
      throw error;
    } finally {
      // 🛡️ 清除上傳標記
      uploadingFiles.current.delete(uploadKey);
    }
  }, [fileManagementContext.actions, demandDispatch, retryOperation]);

  // 🎯 獲取現有檔案的輔助函數
  const getExistingFiles = useCallback((state, formFieldPath) => {
    let current = state;
    for (const field of formFieldPath) {
      if (current && current[field]) {
        current = current[field];
      } else {
        return [];
      }
    }
    return Array.isArray(current) ? current : [];
  }, []);

  // 🎯 通用的移除處理函數
  const handleRemove = useCallback(({ fileId, formField }) => {
    console.log('[useDemandFormFileManagement] handleRemove 參數:', { fileId, formField });

    // 1. 從 DemandContext 移除
    demandDispatch({
      type: ACTIONS.REMOVE_FILE,
      payload: {
        fileId,
        formField: getFormFieldPath(formField)
      }
    });

    // 2. 從 FileManagementContext 移除
    fileManagementContext.actions.removeFile(fileId);
  }, [demandDispatch, fileManagementContext.actions]);

  // 🎯 將表單欄位名稱轉換為 DemandContext 的路徑格式
  const getFormFieldPath = useCallback((fieldName) => {
    // 如果已經是陣列格式，直接返回
    if (Array.isArray(fieldName)) {
      return fieldName;
    }

    // 根據欄位名稱映射到 DemandContext 的路徑
    const fieldMapping = {
      'deadAudio': ['basic', 'voiceFile'],
      'myPublicAudio': ['ceremony', 'public', 'files', 'my', 'audio'],
      'myFamilyAudio': ['ceremony', 'family', 'files', 'my', 'audio'],
      'myFarewellAudio': ['ceremony', 'farewell', 'files', 'my', 'audio'],
      'obituary': ['basic', 'obituary'],
      'myPublicPhoto': ['ceremony', 'public', 'files', 'my', 'photo'],
      'myFamilyPhoto': ['ceremony', 'family', 'files', 'my', 'photo'],
      'myFarewellPhoto': ['ceremony', 'farewell', 'files', 'my', 'photo'],
      'myAction1Photo': ['ceremony', 'actions', '0', 'files', 'my', 'photo'],
      'myAction2Photo': ['ceremony', 'actions', '1', 'files', 'my', 'photo'],
    };

    return fieldMapping[fieldName] || ['unknown', fieldName];
  }, []);

  // 🎯 媒體時長變化處理（已整合到檔案上傳流程中）
  const handleAudioDurationChange = useCallback(({ duration, formField }) => {
    // 媒體時長已在 useFileUpload 中自動處理，此函數保留以維持向後兼容
    console.log('[useDemandFormFileManagement] 媒體時長變化:', { duration, formField });
  }, []);

  // 🎯 檔案狀態檢查
  const hasUploadingFiles = useCallback(() => {
    return fileManagementContext.state.some(file => file.status === 'uploading');
  }, [fileManagementContext.state]);

  const hasFailedFiles = useCallback(() => {
    return fileManagementContext.state.some(file => file.status === 'error');
  }, [fileManagementContext.state]);

  // 🎯 上傳進度獲取
  const getUploadProgress = useCallback(() => {
    const uploadingFiles = fileManagementContext.state.filter(file => file.status === 'uploading');
    if (uploadingFiles.length === 0) return 100;
    
    const totalProgress = uploadingFiles.reduce((sum, file) => sum + (file.uploadProgress || 0), 0);
    return Math.round(totalProgress / uploadingFiles.length);
  }, [fileManagementContext.state]);

  const retryAllFailedFiles = useCallback(async () => {
    const failedFiles = fileManagementContext.state.filter(file => file.status === 'error');
    
    for (const file of failedFiles) {
      try {
        await retryOperation(() => handleFileUpload({
          file: file.originalFile || file,
          fileSource: file.fileSource || 'local',
          formField: file.formField || []
        }));
      } catch (error) {
        console.error(`重試檔案 ${file.name} 失敗:`, error);
      }
    }
    
    console.log('[useDemandFormFileManagement] 重試失敗檔案完成');
  }, [fileManagementContext.state, retryOperation]);

  // 🎯 為提交準備檔案數據
  const prepareFilesForSubmit = useCallback(() => {
    return fileManagementContext.state.filter(file => file.status === 'done').map(file => ({
      id: file.id,
      name: file.name,
      url: file.url,
      type: file.content_type,
      size: file.size,
      duration: file.duration // 包含媒體時長
    }));
  }, [fileManagementContext.state]);

  // 🎯 從舊數據載入檔案（編輯模式）
  const loadFilesFromLegacyData = useCallback(async (formData) => {
    console.log('[useDemandFormFileManagement] 載入檔案從舊數據:', formData);
    // 實現從舊數據載入檔案的邏輯
  }, []);

  return {
    // 主要操作
    handleUpload,
    handleRemove,
    handleAudioDurationChange,

    // 檔案狀態檢查
    hasUploadingFiles,
    hasFailedFiles,
    getUploadProgress,
    retryAllFailedFiles,

    // 數據準備
    prepareFilesForSubmit,
    loadFilesFromLegacyData,

    // 工具函數
    getFormFieldPath,

    // 狀態
    fileState: fileManagementContext.state,
    demandState
  };
}; 